// Application guide section: audience tabs, soft-shaded application areas
// over the figure illustrations, and massage-direction arrows shown only on
// mouse-over (or keyboard focus) of an area card or its shaded region.
//
// A custom element so initialisation survives the theme's morphed page
// transitions and theme-editor section re-renders, where inline section
// scripts do not re-execute.
//
// HOW AREAS RESOLVE ("fake dynamic"): each Application Areas entry on the
// metaobject reads `Label — note`. The label is normalised (letters only),
// looked up in ZONE_ALIASES, and resolved against the figure's landmark
// table below — so writing "Tummy — massage clockwise" in the metafield is
// all it takes for the right body region to light up with the right motion.
// Unknown labels still render as cards, just without a highlight.

const SVG_NS = 'http://www.w3.org/2000/svg';

// Below this width the guide shows one figure at a time and explains the
// selected step with a tooltip on the illustration itself.
const MOBILE_MQ = window.matchMedia('(max-width: 899px)');

// Landmarks per figure, in the 750x1200 canvas of the illustrations
// (assets/application-guide-{figure}-{front|back}.webp).
const LANDMARKS = {
  baby: {
    crown: { x: 378, y: 138, r: 30 },
    forehead: { x: 378, y: 232, rx: 58, ry: 26 },
    temples: [{ x: 293, y: 286, r: 25 }, { x: 468, y: 286, r: 25 }],
    noseBridge: { x: 378, y: 330, rx: 16, ry: 26 },
    mouth: { x: 378, y: 370, r: 20 },
    jaw: { x: 378, y: 400, rx: 62, ry: 18 },
    throat: { x: 378, y: 428, rx: 36, ry: 22 },
    shoulders: [{ x: 302, y: 468, r: 32 }, { x: 458, y: 468, r: 32 }],
    armpits: [{ x: 292, y: 490, r: 21 }, { x: 466, y: 490, r: 21 }],
    chest: { x: 380, y: 528, rx: 88, ry: 66 },
    tummy: { x: 378, y: 638, rx: 88, ry: 60 },
    lowerAbdomen: { x: 378, y: 672, rx: 74, ry: 38 },
    groin: [{ x: 338, y: 778, r: 23 }, { x: 420, y: 778, r: 23 }],
    wrists: [{ x: 192, y: 728, r: 19 }, { x: 545, y: 728, r: 19 }],
    palms: [{ x: 188, y: 768, r: 19 }, { x: 548, y: 768, r: 19 }],
    arms: [{ x: 233, y: 600, rx: 27, ry: 62 }, { x: 522, y: 600, rx: 27, ry: 62 }],
    legs: [{ x: 312, y: 890, rx: 36, ry: 82 }, { x: 448, y: 890, rx: 36, ry: 82 }],
    headBack: { x: 375, y: 250, r: 82 },
    behindEars: [{ x: 272, y: 305, r: 19 }, { x: 472, y: 305, r: 19 }],
    nape: { x: 375, y: 392, rx: 32, ry: 21 },
    upperBack: { x: 375, y: 505, rx: 88, ry: 70 },
    spine: { x: 375, y: 525, rx: 22, ry: 105 },
    lowerBack: { x: 375, y: 614, rx: 72, ry: 38 },
    hips: { x: 375, y: 718, rx: 86, ry: 54 },
    soles: [{ x: 292, y: 1068, rx: 38, ry: 24 }, { x: 438, y: 1068, rx: 38, ry: 24 }],
    bigToes: [{ x: 312, y: 1092, r: 13 }, { x: 418, y: 1092, r: 13 }],
  },
  child: {
    crown: { x: 375, y: 140, r: 23 },
    forehead: { x: 375, y: 188, rx: 44, ry: 18 },
    temples: [{ x: 322, y: 212, r: 21 }, { x: 428, y: 212, r: 21 }],
    noseBridge: { x: 375, y: 225, rx: 11, ry: 19 },
    mouth: { x: 374, y: 268, r: 17 },
    jaw: { x: 375, y: 281, rx: 45, ry: 17 },
    throat: { x: 374, y: 306, rx: 25, ry: 21 },
    shoulders: [{ x: 301, y: 342, r: 27 }, { x: 451, y: 342, r: 27 }],
    armpits: [{ x: 299, y: 386, r: 17 }, { x: 451, y: 386, r: 17 }],
    chest: { x: 375, y: 420, rx: 72, ry: 54 },
    tummy: { x: 375, y: 505, rx: 62, ry: 47 },
    lowerAbdomen: { x: 375, y: 556, rx: 55, ry: 30 },
    groin: [{ x: 343, y: 660, r: 17 }, { x: 408, y: 660, r: 17 }],
    wrists: [{ x: 256, y: 686, r: 15 }, { x: 495, y: 686, r: 15 }],
    palms: [{ x: 252, y: 716, r: 16 }, { x: 498, y: 716, r: 16 }],
    arms: [{ x: 272, y: 520, rx: 21, ry: 66 }, { x: 478, y: 520, rx: 21, ry: 66 }],
    legs: [{ x: 336, y: 890, rx: 27, ry: 92 }, { x: 415, y: 890, rx: 27, ry: 92 }],
    headBack: { x: 372, y: 196, r: 62 },
    behindEars: [{ x: 318, y: 226, r: 15 }, { x: 435, y: 226, r: 15 }],
    nape: { x: 372, y: 291, rx: 21, ry: 17 },
    upperBack: { x: 375, y: 430, rx: 75, ry: 64 },
    spine: { x: 375, y: 460, rx: 18, ry: 100 },
    lowerBack: { x: 375, y: 546, rx: 58, ry: 32 },
    hips: { x: 375, y: 630, rx: 72, ry: 45 },
    soles: [{ x: 318, y: 1068, rx: 31, ry: 19 }, { x: 438, y: 1068, rx: 31, ry: 19 }],
    bigToes: [{ x: 322, y: 1088, r: 12 }, { x: 432, y: 1088, r: 12 }],
  },
  adult: {
    crown: { x: 375, y: 118, r: 21 },
    forehead: { x: 375, y: 141, rx: 42, ry: 18 },
    temples: [{ x: 330, y: 172, r: 19 }, { x: 420, y: 172, r: 19 }],
    noseBridge: { x: 375, y: 183, rx: 10, ry: 18 },
    mouth: { x: 375, y: 228, r: 14 },
    jaw: { x: 375, y: 246, rx: 43, ry: 14 },
    throat: { x: 374, y: 268, rx: 25, ry: 23 },
    shoulders: [{ x: 305, y: 303, r: 27 }, { x: 450, y: 303, r: 27 }],
    armpits: [{ x: 299, y: 353, r: 18 }, { x: 452, y: 353, r: 18 }],
    chest: { x: 375, y: 389, rx: 80, ry: 58 },
    tummy: { x: 375, y: 490, rx: 66, ry: 50 },
    lowerAbdomen: { x: 375, y: 536, rx: 60, ry: 31 },
    groin: [{ x: 341, y: 635, r: 18 }, { x: 411, y: 635, r: 18 }],
    wrists: [{ x: 249, y: 656, r: 16 }, { x: 502, y: 656, r: 16 }],
    palms: [{ x: 246, y: 700, r: 17 }, { x: 505, y: 700, r: 17 }],
    arms: [{ x: 268, y: 480, rx: 23, ry: 72 }, { x: 482, y: 480, rx: 23, ry: 72 }],
    legs: [{ x: 333, y: 875, rx: 29, ry: 108 }, { x: 422, y: 875, rx: 29, ry: 108 }],
    headBack: { x: 375, y: 170, r: 52 },
    behindEars: [{ x: 322, y: 196, r: 15 }, { x: 430, y: 196, r: 15 }],
    nape: { x: 375, y: 251, rx: 23, ry: 18 },
    upperBack: { x: 375, y: 402, rx: 82, ry: 68 },
    spine: { x: 375, y: 432, rx: 19, ry: 108 },
    lowerBack: { x: 375, y: 521, rx: 62, ry: 35 },
    hips: { x: 375, y: 616, rx: 78, ry: 47 },
    soles: [{ x: 312, y: 1068, rx: 33, ry: 20 }, { x: 438, y: 1068, rx: 33, ry: 20 }],
    bigToes: [{ x: 318, y: 1088, r: 13 }, { x: 432, y: 1088, r: 13 }],
  },
};

// The location library: which view a landmark lives on and the massage
// motion drawn for it. `landmark` names an entry in LANDMARKS.
const LOCATIONS = {
  eyebrows: { view: 'front', landmark: 'eyebrows', arrows: 'stroke-out' },
  cheeks: { view: 'front', landmark: 'cheeks', arrows: 'circular' },
  innerankles: { view: 'front', landmark: 'innerAnkles', arrows: 'circular' },
  chest: { view: 'front', landmark: 'chest', arrows: 'radial' },
  upperback: { view: 'back', landmark: 'upperBack', arrows: 'spine' },
  spine: { view: 'back', landmark: 'spine', arrows: 'spine' },
  lowerback: { view: 'back', landmark: 'lowerBack', arrows: 'circular' },
  tummy: { view: 'front', landmark: 'tummy', arrows: 'clockwise' },
  lowerabdomen: { view: 'front', landmark: 'lowerAbdomen', arrows: 'clockwise' },
  throat: { view: 'front', landmark: 'throat', arrows: 'strokes-up' },
  nosebridge: { view: 'front', landmark: 'noseBridge', arrows: 'stroke-down' },
  forehead: { view: 'front', landmark: 'forehead', arrows: 'stroke-out' },
  temples: { view: 'front', landmark: 'temples', arrows: 'circular' },
  jaw: { view: 'front', landmark: 'jaw', arrows: 'stroke-out' },
  mouth: { view: 'front', landmark: 'mouth', arrows: 'circular' },
  behindears: { view: 'back', landmark: 'behindEars', arrows: 'dab' },
  backofhead: { view: 'back', landmark: 'headBack', arrows: 'circular' },
  crown: { view: 'front', landmark: 'crown', arrows: 'dab' },
  nape: { view: 'back', landmark: 'nape', arrows: 'dab' },
  shoulders: { view: 'back', landmark: 'shoulders', arrows: 'circular' },
  armpits: { view: 'front', landmark: 'armpits', arrows: 'dab' },
  groin: { view: 'front', landmark: 'groin', arrows: 'dab' },
  soles: { view: 'back', landmark: 'soles', arrows: 'radial-small' },
  bigtoes: { view: 'back', landmark: 'bigToes', arrows: 'dab' },
  wrists: { view: 'front', landmark: 'wrists', arrows: 'dab' },
  palms: { view: 'front', landmark: 'palms', arrows: 'circular' },
  arms: { view: 'front', landmark: 'arms', arrows: 'strokes-down' },
  legs: { view: 'front', landmark: 'legs', arrows: 'strokes-down' },
  hips: { view: 'back', landmark: 'hips', arrows: 'circular' },
};

// Normalised area-label spellings resolving to LOCATIONS keys. Extend this
// list to teach the guide new phrasings — no other change needed.
const ZONE_ALIASES = {
  cheekpoints: 'cheeks',
  cheeksinuspoints: 'cheeks',
  innerankleslowerlegs: 'innerankles',
  backoftheneckbaseoftheskull: 'nape',
  chest: 'chest',
  chestarea: 'chest',
  breast: 'chest',
  breasts: 'chest',
  back: 'upperback',
  upperback: 'upperback',
  spine: 'spine',
  alongthespine: 'spine',
  lowerback: 'lowerback',
  tummy: 'tummy',
  belly: 'tummy',
  stomach: 'tummy',
  abdomen: 'tummy',
  lowerabdomen: 'lowerabdomen',
  throat: 'throat',
  neck: 'throat',
  bridgeofthenose: 'nosebridge',
  nosebridge: 'nosebridge',
  nose: 'nosebridge',
  sinuses: 'nosebridge',
  forehead: 'forehead',
  brow: 'forehead',
  browarea: 'forehead',
  eyebrows: 'eyebrows',
  alongtheeyebrows: 'eyebrows',
  temples: 'temples',
  templesbrow: 'temples',
  jaw: 'jaw',
  jawline: 'jaw',
  mouth: 'mouth',
  aroundthemouth: 'mouth',
  lips: 'mouth',
  lip: 'mouth',
  behindtheears: 'behindears',
  behindears: 'behindears',
  behindandaroundtheears: 'behindears',
  ears: 'behindears',
  backofthehead: 'backofhead',
  backofhead: 'backofhead',
  crownofthehead: 'crown',
  crown: 'crown',
  napeoftheneck: 'nape',
  nape: 'nape',
  baseoftheskull: 'nape',
  shoulders: 'shoulders',
  shoulder: 'shoulders',
  armpits: 'armpits',
  underthearmpits: 'armpits',
  underarms: 'armpits',
  groin: 'groin',
  groinarea: 'groin',
  innergroin: 'groin',
  innergroinarea: 'groin',
  solesofthefeet: 'soles',
  soles: 'soles',
  feet: 'soles',
  footsoles: 'soles',
  bigtoes: 'bigtoes',
  bigtoe: 'bigtoes',
  wrists: 'wrists',
  wrist: 'wrists',
  pulsepoints: 'wrists',
  palms: 'palms',
  palmsofthehands: 'palms',
  arms: 'arms',
  arm: 'arms',
  legs: 'legs',
  leg: 'legs',
  thighs: 'legs',
  innerthighs: 'legs',
  hips: 'hips',
  buttocks: 'hips',
};

const normalise = (label) => label.toLowerCase().replace(/[^a-z]/g, '');

const splitArea = (raw) => {
  const match = raw.match(/\s*(?:—|–|:|\|)\s*/);
  if (!match) return { label: raw.trim(), note: '' };
  return {
    label: raw.slice(0, match.index).trim(),
    note: raw.slice(match.index + match[0].length).trim(),
  };
};

// Resolve one area key against a figure's landmark table into drawable spots.
LANDMARKS.woman = {
  ...LANDMARKS.adult,
  lowerAbdomen: { x: 374, y: 545, rx: 60, ry: 25 },
  lowerBack: { x: 374, y: 510, rx: 58, ry: 28 },
  innerAnkles: [{ x: 350, y: 1030, rx: 13, ry: 28 }, { x: 399, y: 1030, rx: 13, ry: 28 }],
};

LANDMARKS.face = {
  mouth: { x: 374, y: 696, rx: 68, ry: 24 },
  forehead: { x: 374, y: 340, rx: 100, ry: 50 },
  eyebrows: [{ x: 267, y: 440, rx: 60, ry: 14 }, { x: 482, y: 440, rx: 60, ry: 14 }],
  noseBridge: { x: 374, y: 535, rx: 17, ry: 48 },
  cheeks: [{ x: 254, y: 573, rx: 32, ry: 27 }, { x: 495, y: 573, rx: 32, ry: 27 }],
  temples: [{ x: 178, y: 469, rx: 21, ry: 32 }, { x: 571, y: 469, rx: 21, ry: 32 }],
};

const resolveZone = (figureKind, key, hasFace = false) => {
  const location = LOCATIONS[key];
  if (!location) return null;
  const useFace = hasFace && Boolean(LANDMARKS.face[location.landmark]);
  const landmarks = useFace ? LANDMARKS.face : LANDMARKS[figureKind] || LANDMARKS.child;
  const landmark = landmarks[location.landmark] || (key === 'eyebrows' ? landmarks.forehead : null);
  if (!landmark) return null;
  const raw = Array.isArray(landmark) ? landmark : [landmark];
  const spots = raw.map((spot) => ({
    x: spot.x,
    y: spot.y,
    rx: spot.rx ?? spot.r,
    ry: spot.ry ?? spot.r,
    arrows: location.arrows,
  }));
  return { view: useFace ? 'face' : location.view, spots };
};

// One open-V arrow: a stroked line with a V head at (x2, y2).
const arrowPath = (x1, y1, x2, y2) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = 13;
  const spread = 0.5;
  const ax = x2 - head * Math.cos(angle - spread);
  const ay = y2 - head * Math.sin(angle - spread);
  const bx = x2 - head * Math.cos(angle + spread);
  const by = y2 - head * Math.sin(angle + spread);
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute(
    'd',
    `M ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)} ` +
      `M ${ax.toFixed(1)} ${ay.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)} L ${bx.toFixed(1)} ${by.toFixed(1)}`
  );
  path.setAttribute('pathLength', '100');
  return path;
};

// A near-full circle with an arrowhead. In SVG's y-down space an increasing
// angle sweeps clockwise, matching the "massage in a clockwise motion"
// directions on the tummy blends.
const circularPath = (cx, cy, r) => {
  const start = 0.6;
  const end = start + Math.PI * 1.7;
  const steps = 16;
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = start + ((end - start) * i) / steps;
    points.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
  }
  const d = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
  const [tipX, tipY] = points[steps];
  const [prevX, prevY] = points[steps - 1];
  const angle = Math.atan2(tipY - prevY, tipX - prevX);
  const head = 11;
  const spread = 0.5;
  const ax = tipX - head * Math.cos(angle - spread);
  const ay = tipY - head * Math.sin(angle - spread);
  const bx = tipX - head * Math.cos(angle + spread);
  const by = tipY - head * Math.sin(angle + spread);
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute(
    'd',
    `${d} M ${ax.toFixed(1)} ${ay.toFixed(1)} L ${tipX.toFixed(1)} ${tipY.toFixed(1)} L ${bx.toFixed(1)} ${by.toFixed(1)}`
  );
  path.setAttribute('pathLength', '100');
  return path;
};

const buildArrows = (spot) => {
  const { x, y, rx, ry } = spot;
  const paths = [];
  const gap = 14;

  switch (spot.arrows) {
    case 'radial': {
      paths.push(arrowPath(x, y - gap, x, y - ry + 6));
      paths.push(arrowPath(x, y + gap, x, y + ry - 6));
      paths.push(arrowPath(x - gap, y, x - rx + 6, y));
      paths.push(arrowPath(x + gap, y, x + rx - 6, y));
      break;
    }
    case 'radial-small': {
      paths.push(arrowPath(x, y - 4, x, y - ry - 8));
      paths.push(arrowPath(x, y + 4, x, y + ry + 8));
      break;
    }
    case 'spine': {
      paths.push(arrowPath(x, y - gap, x, y - ry + 10));
      paths.push(arrowPath(x, y + gap, x, y + ry - 10));
      break;
    }
    case 'strokes-up': {
      const lift = ry + 26;
      paths.push(arrowPath(x - rx * 0.55, y + ry * 0.5, x - rx * 0.72, y + ry * 0.5 - lift));
      paths.push(arrowPath(x + rx * 0.55, y + ry * 0.5, x + rx * 0.72, y + ry * 0.5 - lift));
      break;
    }
    case 'strokes-down': {
      const drop = ry * 1.15;
      paths.push(arrowPath(x - rx * 0.4, y - ry * 0.55, x - rx * 0.4, y - ry * 0.55 + drop));
      paths.push(arrowPath(x + rx * 0.4, y - ry * 0.55, x + rx * 0.4, y - ry * 0.55 + drop));
      break;
    }
    case 'stroke-down': {
      paths.push(arrowPath(x, y - ry + 4, x, y + ry - 2));
      break;
    }
    case 'stroke-out': {
      paths.push(arrowPath(x - 6, y, x - rx - 8, y));
      paths.push(arrowPath(x + 6, y, x + rx + 8, y));
      break;
    }
    case 'dab': {
      paths.push(arrowPath(x, y - Math.max(ry, 14) - 12, x, y - 4));
      break;
    }
    case 'clockwise': {
      paths.push(circularPath(x, y, Math.max(rx, ry) * 0.72));
      break;
    }
    case 'circular': {
      paths.push(circularPath(x, y, Math.max(rx, ry) * 0.95));
      break;
    }
    default:
      break;
  }

  return paths;
};

let gradientIdCounter = 0;

class ApplicationGuideSection extends HTMLElement {
  #tourTimer = null;
  #tourDone = false;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.querySelectorAll('[data-guide-panel]').forEach((panel) => this.#initPanel(panel));
    this.#setupTour();

    this.querySelectorAll('[data-guide-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.querySelectorAll('[data-guide-tab]').forEach((other) => {
          other.setAttribute('aria-pressed', String(other === tab));
        });
        this.querySelectorAll('[data-guide-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.guidePanel !== tab.dataset.guideTab;
        });
      });
    });
  }

  // Until the visitor interacts, walk the zones once in step order when the
  // section first scrolls into view — a shop assistant pointing along the body.
  #setupTour() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const cancel = () => this.#stopTour();
    ['pointerdown', 'mouseover', 'focusin'].forEach((type) => this.addEventListener(type, cancel));

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#tourDone && this.#tourTimer === null) {
            io.disconnect();
            this.#runTour();
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(this);
  }

  #stopTour() {
    this.#tourDone = true;
    if (this.#tourTimer !== null) {
      clearInterval(this.#tourTimer);
      this.#tourTimer = null;
    }
  }

  #runTour() {
    const panel = this.querySelector('[data-guide-panel]:not([hidden])');
    if (!panel || !panel._agSetActive) return;
    const keys = [...panel.querySelectorAll('.application-guide__area')]
      .map((item) => item.dataset.zoneKey)
      .filter(Boolean);
    if (keys.length < 2) return;

    let index = 0;
    this.#tourTimer = setInterval(() => {
      index += 1;
      if (index >= keys.length) {
        this.#stopTour();
        panel._agSetActive(keys[0]);
        return;
      }
      panel._agSetActive(keys[index]);
    }, 2200);
  }

  #zoneColor() {
    const value = getComputedStyle(this)
      .getPropertyValue('--application-guide-zone-color')
      .trim();
    return value || '#e87859';
  }

  // A shared radial gradient per overlay: solid at the centre fading to
  // nothing at the edge, so shades sit on the skin like a soft wash.
  #ensureGradient(svg) {
    let id = svg.dataset.gradientId;
    if (id) return id;
    gradientIdCounter += 1;
    id = `application-guide-shade-${gradientIdCounter}`;
    svg.dataset.gradientId = id;

    const color = this.#zoneColor();
    const defs = document.createElementNS(SVG_NS, 'defs');
    const gradient = document.createElementNS(SVG_NS, 'radialGradient');
    gradient.setAttribute('id', id);
    const stops = [
      [0, 0.62],
      [0.55, 0.4],
      [1, 0],
    ];
    for (const [offset, opacity] of stops) {
      const stop = document.createElementNS(SVG_NS, 'stop');
      stop.setAttribute('offset', String(offset));
      stop.setAttribute('stop-color', color);
      stop.setAttribute('stop-opacity', String(opacity));
      gradient.appendChild(stop);
    }
    defs.appendChild(gradient);
    svg.prepend(defs);
    return id;
  }

  #initPanel(panel) {
    const overlays = {};
    panel.querySelectorAll('.application-guide__media').forEach((media) => {
      const overlay = media.querySelector('.application-guide__overlay');
      if (overlay) overlays[media.dataset.figureView] = overlay;
    });
    const firstMedia = panel.querySelector('.application-guide__media');
    const figureKind = firstMedia?.dataset.figureKind || 'child';

    const figuresEl = panel.querySelector('[data-guide-figures]');
    const viewTabs = [...panel.querySelectorAll('[data-view-tab]')];
    const tooltip = panel.querySelector('[data-guide-tooltip]');
    const tooltipLabel = tooltip?.querySelector('[data-tooltip-label]');
    const tooltipNote = tooltip?.querySelector('[data-tooltip-note]');
    const zoneMeta = {};

    panel.querySelectorAll('.application-guide__area').forEach((item) => {
      const { label, note } = splitArea(item.dataset.areaRaw || item.textContent);
      const key = ZONE_ALIASES[normalise(label)] || '';
      item.dataset.zoneKey = key;
      item.textContent = '';

      const labelEl = document.createElement('span');
      labelEl.className = 'application-guide__area-label';
      labelEl.textContent = label;
      item.appendChild(labelEl);

      if (note) {
        const noteWrap = document.createElement('div');
        noteWrap.className = 'application-guide__area-note-wrap';
        const noteInner = document.createElement('div');
        noteInner.className = 'application-guide__area-note-inner';
        const noteEl = document.createElement('span');
        noteEl.className = 'application-guide__area-note';
        noteEl.textContent = note;
        noteInner.appendChild(noteEl);
        noteWrap.appendChild(noteInner);
        item.appendChild(noteWrap);
      }

      const zone = key ? resolveZone(figureKind, key, Boolean(overlays.face)) : null;
      if (!zone || !overlays[zone.view]) return;

      const primarySpot = zone.spots[0];
      zoneMeta[key] = { view: zone.view, x: primarySpot.x, y: primarySpot.y, label, note };

      const svg = overlays[zone.view];
      const gradientId = this.#ensureGradient(svg);

      const group = document.createElementNS(SVG_NS, 'g');
      group.classList.add('application-guide__zone');
      group.dataset.zone = key;
      group.setAttribute('role', 'img');
      group.setAttribute('aria-label', label);

      for (const spot of zone.spots) {
        const shade = document.createElementNS(SVG_NS, 'ellipse');
        shade.classList.add('application-guide__zone-shade');
        shade.setAttribute('cx', spot.x);
        shade.setAttribute('cy', spot.y);
        shade.setAttribute('rx', spot.rx * 1.35);
        shade.setAttribute('ry', spot.ry * 1.35);
        shade.setAttribute('fill', `url(#${gradientId})`);
        group.appendChild(shade);
      }

      const arrows = document.createElementNS(SVG_NS, 'g');
      arrows.classList.add('application-guide__zone-arrows');
      for (const spot of zone.spots) {
        for (const path of buildArrows(spot)) arrows.appendChild(path);
      }
      group.appendChild(arrows);

      svg.appendChild(group);
    });

    const setHot = (key) => {
      panel.querySelectorAll('[data-zone]').forEach((zoneGroup) => {
        zoneGroup.classList.toggle('is-hot', Boolean(key) && zoneGroup.dataset.zone === key);
        zoneGroup.classList.toggle('is-dim', Boolean(key) && zoneGroup.dataset.zone !== key);
      });
      panel.querySelectorAll('.application-guide__area').forEach((areaItem) => {
        areaItem.classList.toggle('is-hot', Boolean(key) && areaItem.dataset.zoneKey === key);
      });
    };

    const setView = (view) => {
      if (figuresEl) figuresEl.dataset.currentView = view;
      viewTabs.forEach((tab) => {
        tab.setAttribute('aria-pressed', String(tab.dataset.viewTab === view));
      });
    };

    const updateTooltip = () => {
      if (!tooltip) return;
      const meta = panel._agActiveKey ? zoneMeta[panel._agActiveKey] : null;
      // Only explain a step the visitor chose themselves — the default
      // selection and the idle tour keep the illustration clean.
      if (!meta || !MOBILE_MQ.matches || !panel._agUserPicked) {
        tooltip.hidden = true;
        return;
      }
      setView(meta.view);
      const media = panel.querySelector(`.application-guide__media[data-figure-view="${meta.view}"]`);
      if (!media) {
        tooltip.hidden = true;
        return;
      }
      media.appendChild(tooltip);
      if (tooltipLabel) tooltipLabel.textContent = meta.label;
      if (tooltipNote) {
        tooltipNote.textContent = meta.note || '';
        tooltipNote.hidden = !meta.note;
      }
      const leftPct = Math.min(Math.max((meta.x / 750) * 100, 16), 84);
      const topPct = (meta.y / 1200) * 100;
      tooltip.style.left = `${leftPct}%`;
      tooltip.style.top = `${topPct}%`;
      tooltip.classList.toggle('is-below', topPct < 20);
      tooltip.hidden = false;
    };

    viewTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        setView(tab.dataset.viewTab);
        if (tooltip) {
          const meta = panel._agActiveKey ? zoneMeta[panel._agActiveKey] : null;
          tooltip.hidden = !meta || meta.view !== tab.dataset.viewTab || !MOBILE_MQ.matches;
        }
      });
    });

    MOBILE_MQ.addEventListener?.('change', updateTooltip);

    // One step is always selected; hovering previews another zone, and a
    // click or tap (on a card or on the body itself) moves the selection.
    const setActive = (key) => {
      panel._agActiveKey = key || null;
      if (overlays.face && zoneMeta[key]) setView(zoneMeta[key].view);
      panel.querySelectorAll('.application-guide__area').forEach((areaItem) => {
        areaItem.classList.toggle('is-active', Boolean(key) && areaItem.dataset.zoneKey === key);
      });
      setHot(panel._agActiveKey);
      updateTooltip();
    };

    panel._agSetHot = setHot;
    panel._agSetActive = setActive;

    const hotTarget = (event) => event.target.closest('[data-zone], .application-guide__area');
    const keyOf = (target) => target.dataset.zone || target.dataset.zoneKey;

    panel.addEventListener('mouseover', (event) => {
      const target = hotTarget(event);
      setHot(target && keyOf(target) ? keyOf(target) : panel._agActiveKey);
    });
    panel.addEventListener('mouseleave', () => setHot(panel._agActiveKey));
    panel.addEventListener('click', (event) => {
      const target = hotTarget(event);
      if (target && keyOf(target)) {
        panel._agUserPicked = true;
        setActive(keyOf(target));
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target.closest('.application-guide__area');
      if (target && target.dataset.zoneKey) {
        event.preventDefault();
        panel._agUserPicked = true;
        setActive(target.dataset.zoneKey);
      }
    });
    panel.addEventListener('focusin', (event) => {
      const target = event.target.closest('.application-guide__area');
      if (target && target.dataset.zoneKey) setHot(target.dataset.zoneKey);
    });
    panel.addEventListener('focusout', () => setHot(panel._agActiveKey));

    const firstItem = [...panel.querySelectorAll('.application-guide__area')].find(
      (areaItem) => areaItem.dataset.zoneKey
    );
    if (firstItem) setActive(firstItem.dataset.zoneKey);
  }
}

if (!customElements.get('application-guide-section')) {
  customElements.define('application-guide-section', ApplicationGuideSection);
}


